import Container from "@/components/ContentContainer/Container";
import InnerContainer from "@/components/ContentContainer/InnerContainer";
import Skeleton from "@/components/Skeleton/Skeleton";
import UsageForm from "@/components/UsageForm/UsageForm";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import useTranslation from "@/hooks/useTranslation";
import styles from "@/pages/products/detail/usage/usage.module.scss";
import { IUsageForm } from "@/pages/products/usage/usageModal";
import { useGetUsage, usePatchUsage } from "@/pages/products/usage/useUsage";
import { CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import { Col, Row } from "antd";
import { useForm } from "antd/es/form/Form";
import _ from "lodash";
import { useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";

function ProductUsage() {
  useTrackOnLoad("Usage Viewed In Product Detail Page");
  const smartQueryClient = useSmartQueryClient();
  const [form] = useForm<IUsageForm>();
  const translation = useTranslation();

  const { productId: id } = useParams();
  const productId = Number(id);

  const { data: companySettings, isLoading: companySettingsIsLoading } = useCompanySettings();

  const includeUsage = companySettings?.include_usage || false;
  const { data, isLoading: dataIsFetching } = useGetUsage({
    productId: productId,
  });

  const patchUsage = usePatchUsage();

  const saveUsage = useCallback(
    (patch: Partial<IUsageForm>) => {
      patchUsage.mutateAsync({ productId, usage: patch });
    },
    [productId]
  );

  const debouncedSaveUsage = useMemo(() => _.debounce(saveUsage, 250), [saveUsage]);

  const onValuesChange = (changedValues: Partial<IUsageForm>) => {
    const electricity_note =
      changedValues?.electricity_notes !== undefined
        ? { electricity_notes: changedValues?.electricity_notes || null }
        : {};

    const lifespan_notes =
      changedValues?.lifespan_notes !== undefined
        ? { lifespan_notes: changedValues?.lifespan_notes || null }
        : {};

    smartQueryClient.setQueryData<IUsageForm>(["usage", productId], (data) => {
      if (!data) return undefined;
      if (changedValues.consumables_used === false || changedValues.consumables_used === null) {
        return { ...data, ...changedValues, consumables: [] };
      }
      return { ...data, ...changedValues };
    });

    debouncedSaveUsage({ ...changedValues, ...electricity_note, ...lifespan_notes });
  };

  const UsageOffContent = (
    <Container padding={false}>
      <InnerContainer title="Usage">
        <Row gutter={[0, 20]}>
          <Col
            span={24}
            className={styles.description}
          >
            {translation.product.usage.description}
          </Col>
          <Col
            span={24}
            className={styles.usageContentContainer}
          >
            {!companySettingsIsLoading ? (
              <div style={{ padding: 16, borderRadius: 16, border: "1px solid #D9D9D9" }}>
                <Row>
                  <Col span={12}>{translation.product.usage.outOfScope}</Col>
                  <Col
                    span={12}
                    style={{ textAlign: "right" }}
                  >
                    <Link to={"/settings"}>{translation.product.usage.manageScope}</Link>
                  </Col>
                </Row>
              </div>
            ) : (
              <Skeleton style={{ height: 70, width: "100%" }} />
            )}
          </Col>
        </Row>
      </InnerContainer>
    </Container>
  );

  const dataState = patchUsage.isLoading ? (
    <>
      <LoadingOutlined /> Saving...
    </>
  ) : (
    <>
      <CheckOutlined /> Saved
    </>
  );

  const UsageOnContent = (
    <UsageForm
      selectedProducts={[productId]}
      form={form}
      data={data}
      isLoading={dataIsFetching}
      onValuesChange={onValuesChange}
      topRight={dataState}
      showStatuses={true}
      disabledText={
        !!data?.proxy_id ? (
          <span>
            This is a proxy product and will inherit the usage from its source product.{" "}
            <Link to={`/products/${data?.proxy_id}/usage`}>
              <span className={styles["proxyRedirectLink"]}>Go to its source</span>
            </Link>{" "}
            to edit the usage.
          </span>
        ) : undefined
      }
    />
  );

  return (
    <Row gutter={[10, 20]}>
      <Col span={24}>{!includeUsage ? UsageOffContent : UsageOnContent}</Col>
    </Row>
  );
}

export default ProductUsage;
